import {memo, useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {sortGuests} from '~/ViewInvitation/components/Guestlist/actions/actions';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {SortBy as SortByTypes, type SortDirection} from '~/ViewInvitation/types/reducer';
import {Button} from '~library/atoms/Button';
import {GenericSpinner} from '~library/atoms/GenericSpinner';
import {Radio} from '~library/atoms/Radio';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {Dropdown} from '~library/molecules/Dropdown';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {SortByIcon} from '~library/svg/SortByIcon';

import * as styles from './SortBy.module.scss';

interface MenuProps {
  onClose?: () => void;
}

const Menu = memo(({onClose}: MenuProps) => {
  const isLargishAndUp = useIsLargishAndUp();
  const {
    state: {sortType, rsvpResponse, sortDirection, isLoading: isSorting},
    dispatch,
  } = useGuestlistDataContext();
  const [isLoading, setIsLoading] = useState(isSorting);
  const [isResetting, setIsResetting] = useState(false);
  const [sort, setSort] = useState<SortByTypes>(sortType);
  const [sortDir, setSortDir] = useState<SortDirection>(sortDirection);

  useEffect(() => {
    setIsLoading(isSorting);
    setIsResetting(isSorting);
  }, [isSorting]);

  const setSorting = (type: SortByTypes, dir: SortDirection) => {
    return useCallback(() => {
      setSort(type);
      setSortDir(dir);
    }, [dir, type]);
  };

  const doSort = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => {
      sortGuests(dispatch, sort, sortDir);
      onClose?.();
    }, 0);
  }, [sort, sortDir, dispatch, onClose]);

  const doReset = useCallback(() => {
    setIsResetting(true);
    setTimeout(() => {
      sortGuests(dispatch, SortByTypes.ALPHA, 'asc');
      onClose?.();
    }, 0);
  }, [dispatch, onClose]);

  return (
    <div className={styles.menu}>
      {isLargishAndUp || (
        <div className={styles.menuHeader}>
          <Typography variant="label2" color="pebble">
            Sort
          </Typography>
          <Button variant="transparent" data-qa-id="close-bottom-sheet" onClick={onClose}>
            <CloseLineIcon ratio={0.75} />
          </Button>
        </div>
      )}
      <Radio
        onChange={setSorting(SortByTypes.ALPHA, 'asc')}
        data-qa-id="sort-by-alpha-asc"
        label="Guest name: A to Z"
        checked={sort === 'alpha' && sortDir === 'asc'}
      />
      <Radio
        onChange={setSorting(SortByTypes.ALPHA, 'desc')}
        data-qa-id="sort-by-alpha-desc"
        label="Guest name: Z to A"
        checked={sort === 'alpha' && sortDir === 'desc'}
      />
      {rsvpResponse === 'undecided' ? (
        <Radio
          onChange={setSorting(SortByTypes.DELIVERY_STATUS, 'asc')}
          data-qa-id="sort-by-delivery-status"
          label="By delivery status"
          checked={sort === 'deliveryStatus'}
        />
      ) : (
        <>
          <Radio
            onChange={setSorting(SortByTypes.RSVP, 'desc')}
            data-qa-id="sort-by-rsvp-desc"
            label="RSVP date: Newest"
            checked={sort === 'rsvp' && sortDir === 'desc'}
          />
          <Radio
            onChange={setSorting(SortByTypes.RSVP, 'asc')}
            data-qa-id="sort-by-rsvp-asc"
            label="RSVP date: Oldest"
            checked={sort === 'rsvp' && sortDir === 'asc'}
          />
          <Radio
            onChange={setSorting(SortByTypes.COMMENTS, 'desc')}
            data-qa-id="sort-by-comments"
            label="By comments"
            checked={sort === 'comments'}
          />
        </>
      )}
      <div className={styles.footer}>
        <Button
          data-qa-id="reset-sort-button"
          onClick={doReset}
          variant="secondary"
          size="large"
          disabled={isResetting || isLoading || isSorting}
        >
          {isResetting && <GenericSpinner size="1rem" />}
          Reset
        </Button>
        <Button
          data-qa-id="reset-sort-button"
          onClick={doSort}
          variant="primary"
          size="large"
          disabled={isResetting || isLoading || isSorting}
        >
          {isLoading && <GenericSpinner size="1rem" />}
          Apply
        </Button>
      </div>
    </div>
  );
});

Menu.displayName = 'Menu';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sheet = memo(({isOpen, onClose}: SheetProps) => {
  return (
    <BottomSheet
      in={isOpen}
      onClose={onClose}
      content={<Menu onClose={onClose} />}
      data-qa-id="guestlist-settings-sheet"
    />
  );
});

Sheet.displayName = 'Sheet';

const SortByButtonTrigger = memo(() => (
  <>
    <SortByIcon ratio={0.5} color="link" />
    <Typography variant="list1" color="link" className={styles.trigger} size="large">
      Sort
    </Typography>
  </>
));

SortByButtonTrigger.displayName = 'SortByButtonTrigger';

export const SortBy = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const isHost = useSelector(selectIsHost);
  const isLargishAndUp = useIsLargishAndUp();
  const {
    state: {guestlist},
  } = useGuestlistDataContext();

  const onClose = useCallback(() => setIsOpen(false), []);

  if (guestlist.length === 0 || !isHost) return null;

  return (
    <div className={styles.container}>
      <Dropdown
        open={isOpen}
        setOpen={setIsOpen}
        buttonVariant="transparent"
        buttonClassName={styles.dropdownTrigger}
        data-qa-value="sort-by-menu-button"
        buttonQaIdModifier="sort-by-menu-button"
        trigger={<SortByButtonTrigger />}
        content={isLargishAndUp && <Menu onClose={onClose} />}
        offsetLeft={false}
      />
      {!isLargishAndUp && <Sheet isOpen={isOpen} onClose={onClose} />}
    </div>
  );
});

SortBy.displayName = 'SortBy';
